/**
 * VPI Data Service
 * Loads VPI (Verbraucherpreisindex) data from JSON file with version-based cache invalidation
 * 
 * Features:
 * - Loads data from /data/vpi_data.json
 * - Caches data in memory for 24 hours
 * - Automatically invalidates cache when version changes
 * - Provides methods to query VPI values by date
 * - Calculates VPI increases between dates
 */

class VPIDataService {
  constructor() {
    this.data = [];
    this.lastUpdated = null;
    this.baseYear = 2020;
    this.loaded = false;
    this.loading = false;
    this.loadPromise = null;
    this.cacheTimestamp = null;
    this.currentVersion = null; // Track current data version
    this.CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours in milliseconds
  }

  /**
   * Load VPI data from JSON file
   * Implements version-based cache invalidation
   */
  async loadData() {
    // If already loading, wait for that promise
    if (this.loading && this.loadPromise) {
      return this.loadPromise;
    }

    // Check if cache is still valid
    const now = Date.now();
    const cacheValid = this.loaded && 
                      this.cacheTimestamp && 
                      (now - this.cacheTimestamp) < this.CACHE_DURATION;

    if (cacheValid) {
      // Cache is valid, but check if version has changed
      try {
        const response = await fetch('/data/vpi_data.json');
        if (response.ok) {
          const jsonData = await response.json();
          
          // If version changed, invalidate cache
          if (jsonData.version && jsonData.version !== this.currentVersion) {
            console.log(`🔄 VPI data version changed: ${this.currentVersion} → ${jsonData.version}`);
            this.loaded = false;
            this.cacheTimestamp = null;
            // Continue to reload data below
          } else {
            // Version unchanged, use cached data
            return;
          }
        }
      } catch (error) {
        console.warn('Version check failed, using cached data:', error);
        return;
      }
    }

    // Need to load/reload data
    this.loading = true;
    this.loadPromise = this._fetchData();
    
    try {
      await this.loadPromise;
    } finally {
      this.loading = false;
      this.loadPromise = null;
    }
  }

  /**
   * Internal method to fetch data from JSON file
   */
  async _fetchData() {
    try {
      console.log('📊 Loading VPI data from JSON file...');
      const response = await fetch('/data/vpi_data.json');
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const jsonData = await response.json();
      
      // Validate data structure
      if (!jsonData.data || !Array.isArray(jsonData.data)) {
        throw new Error('Invalid VPI data structure');
      }

      this.data = jsonData.data;
      this.lastUpdated = jsonData.last_updated;
      this.baseYear = jsonData.base_year || 2020;
      this.currentVersion = jsonData.version || null; // Store version
      this.loaded = true;
      this.isFallback = false; // Live data loaded successfully
      this.cacheTimestamp = Date.now();

      console.log(`✅ VPI data loaded successfully`);
      console.log(`   Version: ${this.currentVersion}`);
      console.log(`   Data points: ${this.data.length}`);
      console.log(`   Latest: ${this.data[0]?.period} = ${this.data[0]?.index_value}`);
      
    } catch (error) {
      console.error('❌ Failed to load VPI data from JSON:', error);
      
      // Fallback to hardcoded data (November 2025)
      console.warn('⚠️  Using fallback VPI data');
      this._loadFallbackData();
    }
  }

  /**
   * Load fallback data if JSON fetch fails
   */
  _loadFallbackData() {
    this.data = [
      { period: '2026-09', index_value: 126.6 },
      { period: '2026-08', index_value: 125.8 },
      { period: '2026-07', index_value: 125.6 },
      { period: '2026-06', index_value: 124.6 },
      { period: '2026-05', index_value: 125 },
      { period: '2026-04', index_value: 125.2 },
      { period: '2026-03', index_value: 124.5 },
      { period: '2026-02', index_value: 123.1 },
      { period: '2026-01', index_value: 122.8 },
      { period: '2025-12', index_value: 122.7 },
      { period: '2025-11', index_value: 122.7 },
      { period: '2025-10', index_value: 123 },
      { period: '2025-09', index_value: 122.6 },
      { period: '2025-08', index_value: 122.3 },
      { period: '2025-07', index_value: 122.2 },
      { period: '2025-06', index_value: 121.8 },
      { period: '2025-05', index_value: 121.8 },
      { period: '2025-04', index_value: 121.7 },
      { period: '2025-03', index_value: 121.2 },
      { period: '2025-02', index_value: 120.8 },
      { period: '2025-01', index_value: 120.3 },
      { period: '2024-12', index_value: 120.5 },
      { period: '2024-11', index_value: 119.9 },
      { period: '2024-10', index_value: 120.2 },
      { period: '2024-09', index_value: 119.7 },
      { period: '2024-08', index_value: 119.7 },
      { period: '2024-07', index_value: 119.8 },
      { period: '2024-06', index_value: 119.4 },
      { period: '2024-05', index_value: 119.3 },
      { period: '2024-04', index_value: 119.2 },
      { period: '2024-03', index_value: 118.6 },
      { period: '2024-02', index_value: 118.1 },
      { period: '2024-01', index_value: 117.6 },
      { period: '2023-12', index_value: 117.4 },
      { period: '2023-11', index_value: 117.3 },
      { period: '2023-10', index_value: 117.8 },
      { period: '2023-09', index_value: 117.8 },
      { period: '2023-08', index_value: 117.5 },
      { period: '2023-07', index_value: 117.1 },
      { period: '2023-06', index_value: 116.8 },
      { period: '2023-05', index_value: 116.5 },
      { period: '2023-04', index_value: 116.6 },
      { period: '2023-03', index_value: 116.1 },
      { period: '2023-02', index_value: 115.2 },
      { period: '2023-01', index_value: 114.3 }
    ];
    this.lastUpdated = new Date().toISOString();
    this.baseYear = 2020;
    this.currentVersion = '2026-09'; // Fallback version (auto-updated 2026-10-17)
    this.loaded = true;
    this.cacheTimestamp = Date.now();
    this.isFallback = true; // Mark as fallback data
  }

  /**
   * Get VPI value for a specific date
   * @param {string|Date} date - Date in format 'YYYY-MM-DD' or Date object
   * @returns {number|null} VPI index value or null if not found
   */
  async getVPIForDate(date) {
    await this.loadData();
    
    const period = this.dateToPeriod(date);
    const entry = this.data.find(item => item.period === period);
    return entry ? entry.index_value : null;
  }

  /**
   * Get the latest available VPI value
   * @returns {{period: string, index_value: number}|null}
   */
  async getLatestVPI() {
    await this.loadData();
    
    if (this.data.length === 0) return null;
    
    // Data is already sorted by period (newest first)
    return this.data[0];
  }

  /**
   * Get all VPI data
   * @returns {Array<{period: string, index_value: number}>}
   */
  async getAllData() {
    await this.loadData();
    return this.data;
  }

  /**
   * Get current data version
   * @returns {string|null} Current version (e.g., "2025-11")
   */
  async getVersion() {
    await this.loadData();
    return this.currentVersion;
  }

  /**
   * Convert date to period format (YYYY-MM)
   * @param {string|Date} date
   * @returns {string} Period in format 'YYYY-MM'
   */
  dateToPeriod(date) {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    const year = dateObj.getFullYear();
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    return `${year}-${month}`;
  }

  /**
   * Calculate VPI increase between two dates
   * @param {string|Date} baseDate - Base date
   * @param {string|Date} currentDate - Current date (optional, defaults to latest)
   * @returns {{baseIndex: number, currentIndex: number, increasePercent: number}|null}
   */
  async calculateIncrease(baseDate, currentDate = null) {
    await this.loadData();
    
    const baseIndex = await this.getVPIForDate(baseDate);
    
    if (!baseIndex) {
      console.warn(`No VPI data found for base date: ${baseDate}`);
      return null;
    }

    let currentIndex;
    if (currentDate) {
      currentIndex = await this.getVPIForDate(currentDate);
      if (!currentIndex) {
        console.warn(`No VPI data found for current date: ${currentDate}`);
        return null;
      }
    } else {
      const latest = await this.getLatestVPI();
      if (!latest) {
        console.warn('No latest VPI data available');
        return null;
      }
      currentIndex = latest.index_value;
    }

    const increasePercent = ((currentIndex - baseIndex) / baseIndex) * 100;
    
    return {
      baseIndex,
      currentIndex,
      increasePercent
    };
  }

  /**
   * Get metadata about the VPI data
   * @returns {{lastUpdated: string, baseYear: number, dataPoints: number, version: string}}
   */
  async getMetadata() {
    await this.loadData();
    
    return {
      lastUpdated: this.lastUpdated,
      baseYear: this.baseYear,
      dataPoints: this.data.length,
      version: this.currentVersion
    };
  }

  /**
   * Force reload data (bypasses cache)
   * Useful for testing or manual refresh
   */
  async forceReload() {
    console.log('🔄 Forcing VPI data reload...');
    this.loaded = false;
    this.cacheTimestamp = null;
    this.currentVersion = null;
    await this.loadData();
  }
}

// Export singleton instance
export default new VPIDataService();
